import { Grid } from '@mui/material';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { useEffect } from 'react';
import { useAppContext } from '../../contexts/AppContext';
import FromSubmitButton from '../ui/forms/FormSubmitButton';
import useFormSubmitErrorAlert from '../ui/forms/useFormSubmitErrorAlert';
import CustomForm, { onSuccessCallback } from '../ui/forms/CustomForm';
import { VALIDATION_ERR_DELAY } from '../../config/AppConstants';
import { ViewOrderExtendedDto } from '../../lib/api/orders/dto/view-order-extended.dto';
import {
  OrderQueryKeys,
  updateOrderDeliveryFee,
} from '../../lib/api/orders/orders.service';
import { UpdateOrderDeliveryFeeDto } from '../../lib/api/orders/dto/update-order-delivery-fee.dto';
import RhfCurrencyInput from '../ui/forms/RhfCurrencyInput';

interface Props {
  order: ViewOrderExtendedDto;
  onSuccess?: onSuccessCallback;
}

export default function UpdateOrderDeliveryFeeForm({
  order,
  onSuccess,
}: Props) {
  const { handleShowMessage } = useAppContext();
  const queryClient = useQueryClient();

  const action = order.deliveryFee.greaterThan(0)
    ? 'Update Order Delivery Fee'
    : 'Add Delivery Fee';

  //Mutations
  const updateMutation = useMutation(
    (updateOrderDeliveryFeeDto: UpdateOrderDeliveryFeeDto) =>
      updateOrderDeliveryFee(order.id, updateOrderDeliveryFeeDto),
  );

  const {
    handleSubmit,
    formState: { isSubmitting, isValid, isSubmitted },
    control,
    setError,
    setFocus,
  } = useForm<UpdateOrderDeliveryFeeDto>({
    resolver: classValidatorResolver(UpdateOrderDeliveryFeeDto),
    mode: 'onTouched',
    defaultValues: {
      deliveryFee: order.deliveryFee.toFixed(2),
    },
    delayError: VALIDATION_ERR_DELAY,
  });

  useEffect(() => {
    setFocus('deliveryFee');
  }, [setFocus]);

  const { clearFormSubmitError, processFormSubmitError, serverErrorMessage } =
    useFormSubmitErrorAlert<UpdateOrderDeliveryFeeDto>();

  const onSubmit: SubmitHandler<UpdateOrderDeliveryFeeDto> = async (
    formData: UpdateOrderDeliveryFeeDto,
  ) => {
    clearFormSubmitError();

    try {
      let result: ViewOrderExtendedDto;

      //Updating
      result = await updateMutation.mutateAsync(formData);
      queryClient.invalidateQueries([
        OrderQueryKeys.findOrderById,
        { id: order.id },
      ]);

      queryClient.invalidateQueries([OrderQueryKeys.findAllOrders]);
      handleShowMessage('Order Delivery Fee Updated');
      onSuccess?.(result);
    } catch (err: any) {
      processFormSubmitError(err, setError);
    }
  };

  return (
    <CustomForm
      serverErrorMessage={serverErrorMessage}
      handleSubmit={handleSubmit(onSubmit)}
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <RhfCurrencyInput
            control={control}
            name="deliveryFee"
            label="Price"
            required
          />
        </Grid>
        <Grid item xs={12}>
          <FromSubmitButton
            isSubmitted={isSubmitted}
            isSubmitting={isSubmitting}
            isValid={isValid}
            text={action}
          />
        </Grid>
      </Grid>
    </CustomForm>
  );
}
